/**
 * ChatSidebar - Ask Blurp.ie sliding panel
 * Real-time environmental intelligence chat interface
 */

import { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useChatStore, selectMessages, useFeatureAccess } from '@/stores';
import { GlassPanel } from '@/components/common';
import { ChatMessage } from './ChatMessage';
import { SystemAlert } from './SystemAlert';
import { ChatInput } from './ChatInput';
import { cn } from '@/utils/cn';
import type { ChatItem } from '@/types';

interface ChatSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isMobile?: boolean;
}

export function ChatSidebar({ isOpen, onClose, isMobile = false }: ChatSidebarProps) {
  const messages = useChatStore(selectMessages);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { hasAccess: hasAiChatAccess } = useFeatureAccess('aiChat');

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const width = isMobile ? '85vw' : '360px';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop for mobile */}
          {isMobile && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={onClose}
            />
          )}

          {/* Sidebar panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className={cn(
              'fixed top-0 right-0 h-full z-50',
              'flex flex-col'
            )}
            style={{ width }}
          >
            <GlassPanel
              className="h-full flex flex-col rounded-none rounded-l-lg overflow-hidden"
              padding="none"
              intensity="heavy"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-glass-border">
                <div className="flex items-center gap-2">
                  <BlurpIcon className="w-5 h-5 text-hud-primary" />
                  <span className="font-hud text-sm text-hud-primary uppercase tracking-wider">
                    Chat
                  </span>
                  {!hasAiChatAccess && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-hud-primary/20 text-hud-primary border border-hud-primary/30">
                      PRO
                    </span>
                  )}
                </div>
                <button
                  onClick={onClose}
                  className={cn(
                    'w-8 h-8 flex items-center justify-center rounded',
                    'text-text-secondary hover:text-hud-primary',
                    'hover:bg-hud-primary/10 transition-colors'
                  )}
                >
                  <CloseIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Feature-gated content */}
              {hasAiChatAccess ? (
                <>
                  {/* Messages area */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {messages.length === 0 ? (
                      <EmptyState />
                    ) : (
                      messages.map((message) => (
                        <MessageRenderer key={message.id} message={message} />
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input area */}
                  <div className="p-4 border-t border-glass-border">
                    <ChatInput />
                  </div>
                </>
              ) : (
                <UpgradePrompt onClose={onClose} />
              )}
            </GlassPanel>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function UpgradePrompt({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
      <div className="w-16 h-16 mb-4 rounded-full bg-hud-primary/20 flex items-center justify-center">
        <svg className="w-8 h-8 text-hud-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-white mb-2">
        AI Chat Assistant
      </h3>
      <p className="text-sm text-text-secondary mb-6">
        Get personalized air quality insights and health recommendations with our AI-powered assistant.
      </p>
      <Link
        to="/pricing"
        onClick={onClose}
        className={cn(
          'inline-flex items-center gap-2 px-6 py-3 rounded-xl',
          'bg-gradient-to-r from-hud-primary to-teal-600',
          'text-white font-semibold',
          'hover:shadow-lg hover:shadow-hud-primary/25',
          'transition-all duration-200'
        )}
      >
        Upgrade to Pro
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
      </Link>
      <p className="text-xs text-text-muted mt-4">
        Unlock all features for $19.99/month
      </p>
    </div>
  );
}

function MessageRenderer({ message }: { message: ChatItem }) {
  if (message.type === 'alert') {
    return <SystemAlert alert={message} />;
  }
  return <ChatMessage message={message} />;
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-4">
      <BlurpIcon className="w-12 h-12 text-hud-primary/30 mb-4" />
      <p className="text-sm text-text-secondary mb-2">
        Environmental Intelligence Active
      </p>
      <p className="text-xs text-text-muted">
        I'll notify you about air quality changes and optimal routes.
      </p>
    </div>
  );
}

// Blurp.ie logo icon
function BlurpIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
    </svg>
  );
}

// Close icon
function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export default ChatSidebar;
